import { IBoundingBox, IBoundingSphere, IMatrix4, ITransformedBoundingBox, IVector3 } from '../../api.js';
import { Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
export function get_perspective_framed_bounds(bounds, projection, direction, loose_up, framing_target, margin_world, margin_viewport) {
    if (Math.abs(IVector3.dot(direction, loose_up)) > 0.999) {
        throw new Error("'direction' and 'up' parameters are parallel.");
    }
    // If the margin is => 0.5, there's no space left for the actual object.
    if (Math.abs(margin_viewport) >= 0.5) {
        throw new Error("'margin_viewport' for framing must be in the range -0.5 to 0.5 (exclusive)");
    }
    const camera_matrix = IMatrix4.look_at(new IVector3(0, 0, 0), direction, loose_up);
    const view_matrix = IMatrix4.orthonormal_rotation_affine_transform_inverse(camera_matrix);
    if (framing_target === null) {
        // Calculate center of our total bounding volume for camera positioning
        const view_space_bounds = IBoundingBox.Invalid();
        for (let i = 0; i < bounds.length; i++) {
            const bound = bounds[i];
            let view_space_transformed_bounding_box;
            if (IBoundingBox.is(bound)) {
                if (!IBoundingBox.is_valid(bound))
                    continue;
                view_space_transformed_bounding_box = new ITransformedBoundingBox(bound, view_matrix);
            }
            else if (IBoundingSphere.is(bound)) {
                if (!IBoundingSphere.is_valid(bound))
                    continue;
                view_space_transformed_bounding_box = new ITransformedBoundingBox(IBoundingSphere.to_bounding_box(bound), view_matrix);
            }
            else if (ITransformedBoundingBox.is(bound)) {
                if (!ITransformedBoundingBox.is_valid(bound))
                    continue;
                view_space_transformed_bounding_box = new ITransformedBoundingBox(bound.local_bounding_box, IMatrix4.multiply(view_matrix, bound.transform));
            }
            else {
                throw new Error("Invalid bounds type");
            }
            const view_space_aabb = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(view_space_transformed_bounding_box);
            IBoundingBox.encapsulate_in_place(view_space_bounds, view_space_aabb.min, view_space_aabb.max);
        }
        const view_space_center = IVector3.scale(IVector3.add(view_space_bounds.min, view_space_bounds.max), -0.5);
        IVector3.copy_to_ref(view_space_center, view_matrix[3]);
        IMatrix4.orthonormal_rotation_affine_transform_inverse_to_ref(view_matrix, camera_matrix);
        framing_target = IVector3.copy(camera_matrix[3]);
    }
    /*
      We will be calculating our framing as the distance required to move the camera from the center of the bounding volume.
      To calculate this distance, we need to consider our field of view, which is what these "perspective_scaling" variables are for.
  
      //Consider that we split our view frustum in half along a central plane (this is why we half our field of view).
  
      We simplify our problem by only considering half our view frustum, and thus only half of our field-of-view angle.
      We do that by "splitting" our frustom by a plane passing through the origin (in view space, so our camera's position in world space),
      and is a horizontal plane for our horizontal case, and vertical for our vertical case.
      This means that for our horizontal case, we will only be considering the absolute value of our view space vertices' x-components, and same for y-components in our vertical case.
  
      In view space, if we have a vertex V, then the field of view we need for horizontal fitting should satisfy the below equations:
        (V.x and V.y are known, V.z is the distance we need to calculate for framing)
        (length(V) is thus also unknown, and is the distance from the fitted camera to the vertex)
  
        1) sin(horizontal_fov / 2) * length(V) = abs(V.x)
        2) cos(horizontal_fov / 2) * length(V) = V.z
  
        Then we divide 2) by 1) and we get:
  
            cos(horizontal_fov / 2) * length(V)
            ----------------------------------- = V.z / abs(V.x)
            sin(horizontal_fov / 2) * length(V)
  
        Now our unknown expression length(V) is canceled out and we get:
  
            cos(horizontal_fov / 2) / sin(horizontal_fov / 2) = V.z / abs(V.x) =>
            cot(horizontal_fov / 2) * abs(V.x) = V.z
  
        and thus we have calculated our unknown V.z to satisfy that we fit the object!
        This solution is monotonic with regards to V.x, so we only need to calculate this for the "extreme" values of V, which means our AABB's vertices is enough.
        The solution is the same for vertical field of view, except you would use V.y instead of V.x.
    */
    // These are really cot(0.5 * fov), but javascript doesn't supply that.
    const horizontal_perspective_scaling = 1.0 / Math.tan(0.5 * projection.horizontal_fov);
    const vertical_perspective_scaling = 1.0 / Math.tan(0.5 * projection.vertical_fov);
    const viewport_margin_scale = 1.0 / (1.0 - 2.0 * margin_viewport);
    let view_space_bounds_min_z = Number.MAX_VALUE;
    let view_space_bounds_max_z = -Number.MAX_VALUE;
    let distance = -Number.MAX_VALUE;
    // Calculate the maximum distance we need for the camera to fit all our vertices
    for (const data of bounds) {
        if (IBoundingBox.is(data) || ITransformedBoundingBox.is(data)) {
            let view_space_transformed_bounding_box;
            if (IBoundingBox.is(data)) {
                if (!IBoundingBox.is_valid(data))
                    continue;
                view_space_transformed_bounding_box = new ITransformedBoundingBox(data, view_matrix);
            }
            else if (ITransformedBoundingBox.is(data)) {
                if (!ITransformedBoundingBox.is_valid(data))
                    continue;
                view_space_transformed_bounding_box = new ITransformedBoundingBox(data.local_bounding_box, IMatrix4.multiply(view_matrix, data.transform));
            }
            else {
                throw new Error("Invalid bounds type");
            }
            const vertices = ITransformedBoundingBox.get_transformed_vertices(view_space_transformed_bounding_box);
            for (let i = 0; i < vertices.length; i++) {
                const transformed_vertex = vertices[i];
                const to_near_plane = transformed_vertex.z;
                distance = Math.max(distance, to_near_plane + (Math.abs(transformed_vertex.x) + margin_world) * horizontal_perspective_scaling * viewport_margin_scale, to_near_plane + (Math.abs(transformed_vertex.y) + margin_world) * vertical_perspective_scaling * viewport_margin_scale);
                // Also calculate our viewspace z-bounds while we're at it.
                view_space_bounds_min_z = Math.min(view_space_bounds_min_z, transformed_vertex.z);
                view_space_bounds_max_z = Math.max(view_space_bounds_max_z, transformed_vertex.z);
            }
        }
        else if (IBoundingSphere.is(data)) {
            const view_space_center = IMatrix4.transform_vector3(view_matrix, data.position);
            const to_near_plane = view_space_center.z;
            distance = Math.max(distance, to_near_plane + (data.radius + margin_world) * horizontal_perspective_scaling * viewport_margin_scale, to_near_plane + (data.radius + margin_world) * vertical_perspective_scaling * viewport_margin_scale);
            view_space_bounds_min_z = Math.min(view_space_bounds_min_z, to_near_plane - data.radius);
            view_space_bounds_max_z = Math.max(view_space_bounds_max_z, to_near_plane + data.radius);
        }
        else {
            throw new Error("Invalid bounds type");
        }
    }
    if (distance === -Number.MAX_VALUE) {
        throw new Error("'Failed to frame any objects.'");
    }
    IVector3.add_to_ref(framing_target, IVector3.scale(direction, -distance), camera_matrix[3]);
    // Make sure the given projection's near/far planes are sufficient for our framing.
    const bounds_z_range = (view_space_bounds_max_z - view_space_bounds_min_z);
    const framing = {
        camera_matrix: camera_matrix,
        z_near: distance - 0.5 * bounds_z_range,
        z_far: distance + 0.5 * bounds_z_range,
        framing_target_position: framing_target
    };
    return framing;
}
export function get_precise_framed_perspective_camera_matrix(viewer, object_ids, projection, direction, loose_up, include_child_objects, margin_world, margin_viewport) {
    /*
    This function is kind of a WIP, as I need to figure out how to fit the objects more tightly (not positioning camera from volume center).
    There is quite a lot of code duplication from get_box_framed_perspective_camera_matrix as it's pretty much the same algorithm,
    but this will probably change.
    As such I don't explain the algorithm here, but look at get_box_framed_perspective_camera_matrix for explanation.
    Difference is that we use the model's raw vertices, instead of just their AABB's vertices.
    --Tore
  */
    if (Math.abs(IVector3.dot(direction, loose_up)) > 0.999) {
        return Promise.reject(new Error("'direction' and 'up' parameters are parallel."));
    }
    // If the margin is => 0.5, there's no space left for the actual object.
    if (Math.abs(margin_viewport) >= 0.5) {
        return Promise.reject(new Error("'margin_viewport' for framing must be in the range -0.5 to 0.5 (exclusive)"));
    }
    // TODO: Change algorithm to put the camera origin so that the objects are precisely framed, instead of putting camera origin on volume center.
    const camera_matrix = new BABYLON_Matrix();
    const direction_babylon = new BABYLON_Vector3();
    const up_babylon = new BABYLON_Vector3();
    const view_matrix = camera_matrix.clone();
    BABYLON_Matrix.LookAtRHToRef(BABYLON_Vector3.ZeroReadOnly, direction_babylon.fromIVector3(direction), up_babylon.fromIVector3(loose_up), view_matrix);
    view_matrix.invertToRef(camera_matrix);
    const all_objects_ready_promises = [];
    const objects = viewer.viewer_scene.get_objects(object_ids);
    if (include_child_objects) {
        const parent_objects_length = objects.length;
        for (let i = 0; i < parent_objects_length; i++) {
            objects[i].children_iterator(true, false, (object) => {
                objects.push(object);
            });
        }
    }
    for (let i = 0; i < objects.length; i++) {
        all_objects_ready_promises.push(objects[i].ready);
    }
    return Promise.all(all_objects_ready_promises).then(() => {
        const camera_position = new BABYLON_Vector3();
        const cache_model_view_matrix = new BABYLON_Matrix();
        const viewspace_bounds = {
            min: new BABYLON_Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE),
            max: new BABYLON_Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)
        };
        for (let i = 0; i < objects.length; i++) {
            objects[i].get_world_matrix().multiplyToRef(view_matrix, cache_model_view_matrix);
            objects[i].traverse_vertices_local_space((vertex) => {
                IVector3.min_to_ref(viewspace_bounds.min, vertex, viewspace_bounds.min);
                IVector3.max_to_ref(viewspace_bounds.max, vertex, viewspace_bounds.max);
            }, cache_model_view_matrix);
        }
        const viewspace_center = IVector3.scale(IVector3.add(viewspace_bounds.min, viewspace_bounds.max), 0.5);
        BABYLON_Vector3.TransformCoordinatesFromFloatsToRef(viewspace_center.x, viewspace_center.y, viewspace_center.z, camera_matrix, camera_position);
        camera_matrix.setTranslation(camera_position);
        camera_matrix.invertToRef(view_matrix);
        const horizontal_perspective_scaling = Math.tan(0.5 * projection.horizontal_fov) * (1.0 - 2.0 * margin_viewport);
        const vertical_perspective_scaling = Math.tan(0.5 * projection.vertical_fov) * (1.0 - 2.0 * margin_viewport);
        let distance = -Number.MAX_VALUE;
        let min_z = Number.MAX_VALUE;
        let max_z = -Number.MAX_VALUE;
        for (let i = 0; i < objects.length; i++) {
            objects[i].get_world_matrix().multiplyToRef(view_matrix, cache_model_view_matrix);
            objects[i].traverse_vertices_local_space((vertex) => {
                const to_near_plane = vertex.z;
                distance = Math.max(distance, to_near_plane + (Math.abs(vertex.x) + margin_world) / horizontal_perspective_scaling, to_near_plane + (Math.abs(vertex.y) + margin_world) / vertical_perspective_scaling);
                min_z = Math.min(min_z, vertex.z);
                max_z = Math.max(max_z, vertex.z);
            }, cache_model_view_matrix);
        }
        if (distance === -Number.MAX_VALUE) {
            return Promise.reject(new Error("'Failed to frame any objects.'"));
        }
        camera_matrix.setTranslation(camera_position.add(direction_babylon.scale(-distance)));
        camera_matrix.invertToRef(view_matrix);
        const bounds_z_range = max_z - min_z;
        const framing = {
            camera_matrix: [
                new BABYLON_Vector4(),
                new BABYLON_Vector4(),
                new BABYLON_Vector4(),
                new BABYLON_Vector4()
            ],
            z_near: distance - 0.5 * bounds_z_range,
            z_far: distance + 0.5 * bounds_z_range,
            framing_target_position: camera_position
        };
        camera_matrix.toIMatrix4ToRef(framing.camera_matrix);
        return framing;
    });
}
//# sourceMappingURL=ObjectFraming.js.map